import classNames from "classnames";
import { memo } from "react";
import type { IntakeQuestion } from "../../registry/types";
import * as css from "./intake-modal.module.css";

type IntakeOptionLayout = NonNullable<IntakeQuestion["layout"]>;
type IntakeOptionData = IntakeQuestion["options"][number];

const OPTION_REVEAL_DELAY_CLASSES = [
  css.optionRevealDelay0,
  css.optionRevealDelay1,
  css.optionRevealDelay2,
  css.optionRevealDelay3,
  css.optionRevealDelay4,
  css.optionRevealDelay5,
  css.optionRevealDelay6,
  css.optionRevealDelay7,
  css.optionRevealDelay8,
  css.optionRevealDelay9,
  css.optionRevealDelay10,
  css.optionRevealDelay11,
] as const;

export interface IntakeOptionProps {
  option: IntakeOptionData;
  layout: IntakeOptionLayout;
  isSelected: boolean;
  animateReveal?: boolean;
  className?: string;
  disabled?: boolean;
  onBlur?: () => void;
  onClick: () => void;
  onFocus?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  revealIndex?: number;
}

/**
 * One option in the intake question. Two layouts share the same base
 * surface (`.option`) and selected state (`.optionSelected`); each
 * adds its own shape + chrome:
 *   - "list" — full-width card with icon-box + label + radio dot.
 *   - "grid" — compact pill with icon + label, no radio (selection
 *     communicated by the border/bg alone).
 */
export const IntakeOption = memo(function IntakeOption({
  option,
  layout,
  isSelected,
  animateReveal = false,
  className: customClassName,
  disabled,
  onBlur,
  onClick,
  onFocus,
  onMouseEnter,
  onMouseLeave,
  revealIndex = 0,
}: IntakeOptionProps) {
  const { Icon } = option;
  const className = classNames(
    css.option,
    layout === "grid" ? css.optionPill : css.optionCard,
    isSelected && css.optionSelected,
    animateReveal && css.optionReveal,
    animateReveal &&
      OPTION_REVEAL_DELAY_CLASSES[
        Math.min(
          Math.max(0, revealIndex),
          OPTION_REVEAL_DELAY_CLASSES.length - 1,
        )
      ],
    customClassName,
  );
  return (
    <button
      aria-pressed={isSelected}
      className={className}
      disabled={disabled}
      onBlur={onBlur}
      onClick={onClick}
      onFocus={onFocus}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      type="button"
    >
      {layout === "grid" ? (
        <>
          {Icon ? <Icon className={css.optionPillIcon} /> : null}
          <span className={css.optionPillLabel}>{option.label}</span>
          {option.badges?.map((badge) => (
            <span className={css.optionPillBadge} key={badge}>
              {badge}
            </span>
          ))}
        </>
      ) : (
        <>
          <span className={css.optionContent}>
            {Icon ? (
              <span className={css.optionIconBox}>
                <Icon className={css.optionIcon} />
              </span>
            ) : null}
            <span className={css.optionLabel}>{option.label}</span>
          </span>
          <span className={css.optionRadio}>
            <span className={css.optionRadioInner} />
          </span>
        </>
      )}
    </button>
  );
});
